import type { ComponentProps } from 'react';
import { TouchLock } from '../index';

import type { useLoggerContext } from './loggerContext';
import { NoteComposer } from './NoteComposer';
import { TimelineList } from './TimelineList';

type Ctx = ReturnType<typeof useLoggerContext>;

// The bottom of the timeline logger: the note composer and the
// reverse-chronological feed. Split out of TimelineLoggerScreen
// when LG-29's rest-freeze wraps pushed the screen over the 500-line cap (CN-27
// had already flagged it at 496 — "the next touch splits first, not during").
// Pure render shell: it reads the same context bag the screen does (mirroring
// useTimelineLogger(ctx)) plus the feed/handlers the screen derives, and owns no
// state of its own.
//
// LG-29: the feed sits in a <TouchLock> so a rest freezes it too — a mini-card
// tap navigates away from the resting set, which is a cold action (tap the
// timer's ✕ first). ⚠️ The prop is named for the boolean it actually receives.
// It used to be called `restFrozen` while the screen bound it to
// `restFreezesNavigation`, and that mismatch cost an audit an hour reading the
// add-exercise freeze as a regression it was not (a11y audit, fetta E). The note composer stays LIVE on purpose: jotting a note
// mid-rest touches no set and is a genuinely useful thing to do while the clock
// runs. The add-exercise button carried the same freeze and took it with it to
// the roster strip, whose own <TouchLock> already covers it.
export function TimelineFeedSection({
  ctx,
  feed,
  restFreezesNavigation,
  onSendNote,
  onDeleteNote,
  onFocusRow,
  bestE1rmForSlug,
  failFlash,
}: {
  ctx: Ctx;
  feed: ComponentProps<typeof TimelineList>['feed'];
  /** A rest is counting on the active card → freeze the navigation surfaces.
   *  ⛔ NOT the screen's blunt `restFrozen`: this one is false on a last-set
   *  rest (TM-12), because there the athlete's next move IS to navigate. */
  restFreezesNavigation: boolean;
  onSendNote: ComponentProps<typeof NoteComposer>['onSend'];
  onDeleteNote: ComponentProps<typeof TimelineList>['onDeleteNote'];
  onFocusRow: ComponentProps<typeof TimelineList>['onFocusRow'];
  bestE1rmForSlug: ComponentProps<typeof TimelineList>['bestE1rmForSlug'];
  failFlash: ComponentProps<typeof TimelineList>['flashRow'];
}) {
  const {
    draft,
    theme,
    t,
    i18n,
    getExerciseNameBySlug,
    groupLabelFor,
  } = ctx;
  return (
    <>
      {/* Notes + feed live OUTSIDE the exercise gate — a note isn't tied to
          an exercise, so the composer is visible from the very START and
          detached from the focus card (Antenore 2026-06-04, the design
          intent: "appena prima della timeline"). Both render whether or not
          any exercise has been added. Composer stays LIVE during a rest. */}
      <NoteComposer onSend={onSendNote} t={t} theme={theme} />

      {/* Add-exercise used to sit here, between the note composer and the feed
          (inbox bdfb84a3). It moved to the roster strip's fixed slot (PE-22
          fetta E): down here it was below the fold as soon as a session had a
          few sets in it, and it was one of four hand-rolled copies. */}

      {/* Chronological feed: logged sets + note mini-cards, interleaved by
          time (oldest first). Frozen during a rest (LG-29): a mini-card tap
          re-focuses that set's exercise — a cold navigation, not a mid-rest
          one. */}
      <TouchLock locked={restFreezesNavigation}>
        <TimelineList
          feed={feed}
          startedAt={draft.startedAt}
          resolveName={getExerciseNameBySlug}
          groupLabel={(groupId, kind) =>
            groupId
              ? groupLabelFor({ id: groupId, kind: kind ?? 'superset' })
              : null
          }
          onFocusRow={onFocusRow}
          onDeleteNote={onDeleteNote}
          bestE1rmForSlug={bestE1rmForSlug}
          flashRow={failFlash}
          locale={i18n.language}
          theme={theme}
        />
      </TouchLock>
    </>
  );
}
